import { Button, Card, Modal, Grid, Input } from '@design-systems/canvas-ui'
import { useCallback, useMemo, useState } from 'react'
import Icon, { PencilIcon, TrashIcon } from '@design-systems/react-icons'
import { NoteItem } from './note-types'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useLogClick } from 'utils/analytics'

export type NotesItemsProps = {
    title: string
    type: string
    addLabel: string
    notes: NoteItem[]
    setNotes: (notes: NoteItem[] | ((prev: NoteItem[]) => NoteItem[])) => void
    handleAdd: (body: NoteItem) => void
    handleUpdate: (body: NoteItem) => void
    formattedDate: string
    isInProgress: boolean
    isCheckboxEnabled: boolean
    hasWritePermission: boolean
}

export const NotesItems = ({
    title,
    type,
    addLabel,
    notes,
    setNotes,
    handleAdd,
    handleUpdate,
    formattedDate,
    isInProgress,
    isCheckboxEnabled,
    hasWritePermission,
}: NotesItemsProps) => {
    const { notes: notesKeys } = TRANSLATION_KEYS
    const { items: itemsKeys } = notesKeys
    const { t, currentLocation, session } = useEssentials()
    const { logClick } = useLogClick('notes')

    const visibleItems = useMemo(() => {
        return notes
            .filter((item) => item.type === type && !item.deleted)
            .sort((a, b) => {
                const aTs = a.created_timestamp || ''
                const bTs = b.created_timestamp || ''
                if (!aTs && !bTs) return 0
                if (!aTs) return 1
                if (!bTs) return -1
                return aTs.localeCompare(bTs)
            })
    }, [notes, type])

    const [inputValue, setInputValue] = useState('')
    // Editing state for an existing item
    const [editingItemId, setEditingItemId] = useState<number | null>(null)
    const [editingValue, setEditingValue] = useState('')
    // Delete confirmation modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)

    const handleAddItemClick = () => {
        logClick(`add ${type}`)
        const ts = new Date().toISOString()
        const newItem: NoteItem = {
            type: title,
            completed: false,
            deleted: false,
            created_timestamp: ts,
            last_updated_timestamp: ts,
            description: inputValue,
        }

        setNotes((prev: NoteItem[]) => [...prev, newItem])
        handleAdd({
            store_id: currentLocation.slice(1),
            notes_date: formattedDate,
            type,
            description: inputValue,
            last_updated_user: session?.userInfo?.lanId,
        } as any)
        setInputValue('')
    }

    const updateItem = useCallback(
        (id: number, transform: (item: NoteItem) => NoteItem) => {
            const currentItem = notes.find((item) => item.id === id)!
            const updatedItem = transform(currentItem)
            handleUpdate({
                id: updatedItem.id,
                completed: updatedItem.completed,
                deleted: updatedItem.deleted,
                last_updated_user: session?.userInfo?.lanId,
                description: updatedItem.description,
            })
        },
        [notes, handleUpdate, session],
    )

    const handleDelete = useCallback(
        (id: number) => {
            logClick(`delete ${type}`)
            updateItem(id, (item) => ({
                ...item,
                id,
                deleted: true,
                last_updated_timestamp: new Date().toISOString(),
            }))
        },
        [updateItem, logClick, type],
    )

    const handleDeleteClick = useCallback((id: number) => {
        setItemToDelete(id)
        setDeleteModalOpen(true)
    }, [])

    const handleConfirmDelete = useCallback(() => {
        if (itemToDelete !== null) {
            handleDelete(itemToDelete)
        }
        setDeleteModalOpen(false)
        setItemToDelete(null)
    }, [itemToDelete, handleDelete])

    const handleCancelDelete = useCallback(() => {
        setDeleteModalOpen(false)
        setItemToDelete(null)
    }, [])

    const toggleComplete = useCallback(
        (id: number) => {
            logClick(`toggle ${type} complete`)
            updateItem(id, (item) => ({
                ...item,
                id,
                completed: !item.completed,
                last_updated_timestamp: new Date().toISOString(),
            }))
        },
        [updateItem, logClick, type],
    )

    const handleBeginEdit = (id: number, description: string) => {
        logClick(`edit ${type}`)
        setEditingItemId(id)
        setEditingValue(description)
    }

    const handleSaveEdit = (id: number) => {
        if (isInProgress || editingValue.trim() === '') return
        logClick(`save ${type} edit`)
        updateItem(id, (orig) => ({
            ...orig,
            description: editingValue.trim(),
            last_updated_timestamp: new Date().toISOString(),
        }))
        setEditingItemId(null)
        setEditingValue('')
    }

    const isNotesType = type === 'notes'
    const wrapperClassName = isNotesType
        ? 'item notes-type hc8-mb-4x hc8-pa-3x'
        : 'item'
    return (
        <>
            <h3 className="hc8-mt-6x">{title}</h3>
            {visibleItems.map((item, index) => {
                const itemKey = item.id || `item-${index}`

                const content = (
                    <>
                        {editingItemId === item.id && hasWritePermission ? (
                            <div className="editing-block hc8-mb-1x">
                                <Input.Textarea
                                    aria-label={`edit-${item.id}`}
                                    value={editingValue}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLTextAreaElement>,
                                    ) => setEditingValue(e.target.value)}
                                    isDisabled={isInProgress}
                                    className="editing-textarea full-width-textarea"
                                    rows={Math.max(
                                        1,
                                        editingValue.split('\n').length,
                                    )}
                                />
                                <Button
                                    isDisabled={
                                        isInProgress ||
                                        editingValue.trim() === ''
                                    }
                                    onClick={() => handleSaveEdit(item.id!)}
                                    className="editing-save-button"
                                    variant="primary"
                                >
                                    {t(itemsKeys.updateButton)}
                                </Button>
                            </div>
                        ) : (
                            <div key={itemKey} className="item note-row">
                                <div className="note-row__content">
                                    {isCheckboxEnabled ? (
                                        <Input.Checkbox
                                            id={`${title}-${item.id}`}
                                            label={item.description}
                                            onClick={() =>
                                                toggleComplete(item.id!)
                                            }
                                            isChecked={item.completed}
                                            isDisabled={
                                                isInProgress ||
                                                !hasWritePermission
                                            }
                                            aria-label={item.description}
                                        />
                                    ) : (
                                        <span className="note-row__text">
                                            {item.description}
                                        </span>
                                    )}
                                </div>

                                {!isInProgress && hasWritePermission && (
                                    <div className="note-row__actions">
                                        <Button
                                            isIconOnly
                                            variant="ghost"
                                            size="sm"
                                            aria-label={t(itemsKeys.editButton)}
                                            onClick={() =>
                                                handleBeginEdit(
                                                    item.id!,
                                                    item.description,
                                                )
                                            }
                                        >
                                            <Icon
                                                svg={PencilIcon}
                                                aria-hidden
                                            />
                                        </Button>
                                        <Button
                                            isIconOnly
                                            variant="ghost"
                                            size="sm"
                                            aria-label={t(
                                                itemsKeys.deleteButton,
                                            )}
                                            onClick={() =>
                                                handleDeleteClick(item.id!)
                                            }
                                            data-testid={`delete-${item.id}`}
                                        >
                                            <Icon svg={TrashIcon} aria-hidden />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )

                return isNotesType ? (
                    <Card key={itemKey} className={wrapperClassName}>
                        {content}
                    </Card>
                ) : (
                    <div key={itemKey} className={wrapperClassName}>
                        {content}
                    </div>
                )
            })}
            {hasWritePermission && (
                <Card>
                    <div className={'item-add'}>
                        <div className="add-textarea-wrapper">
                            <div>
                                <Input.Label>{addLabel}</Input.Label>
                            </div>
                            <div className="textarea-button-row">
                                <Input.Textarea
                                    aria-label={addLabel}
                                    value={inputValue}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLTextAreaElement>,
                                    ) => setInputValue(e.target.value)}
                                    isDisabled={isInProgress}
                                    rows={Math.max(
                                        1,
                                        inputValue.split('\n').length,
                                    )}
                                    className="full-width-textarea hc8-mr-3x"
                                />
                                <div className="hc8-pt-1x">
                                    <Button
                                        onClick={handleAddItemClick}
                                        isDisabled={
                                            isInProgress ||
                                            inputValue.trim() === ''
                                        }
                                        variant="primary"
                                    >
                                        {t(itemsKeys.addButton)}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
            <Modal
                headingText={t(itemsKeys.deleteConfirmTitle)}
                isOpen={deleteModalOpen}
                onClose={handleCancelDelete}
                footer={
                    <Grid.Container spacing="dense" direction="row-reverse">
                        <Grid.Item>
                            <Button
                                onClick={handleConfirmDelete}
                                variant="primary"
                            >
                                {t(itemsKeys.confirmButton)}
                            </Button>
                        </Grid.Item>
                        <Grid.Item>
                            <Button
                                onClick={handleCancelDelete}
                                variant="secondary"
                            >
                                {t(itemsKeys.cancelButton)}
                            </Button>
                        </Grid.Item>
                    </Grid.Container>
                }
            />
        </>
    )
}
